import { Auth } from "aws-amplify";
import axios from "axios";
import { COGNITO } from "../generated-aws";
import {
  EmailTemplate,
  EmailTemplateList,
  Notification,
} from "../models/notification";

const { API_HOST } = COGNITO;
interface SubmitForReviewParams {
  emailSubject: string;
  emailTemplate: string;
  emailTemplateVariables: Record<string, any>;
  fromEmail: string;
  replyToEmail: string;
  recipientsCSV?: File;
  emailTo?: string;
}

interface ImportRecipientsParams {
  emailSubject: string;
  emailTemplate: string;
  emailTemplateVariables: Record<string, any>;
  recipientsCSV?: File;
  fromEmail?: string;
  replyToEmail?: string;
}

interface SendTestEmailParams {
  emailTo: string;
  emailSubject: string;
  emailBody: string;
  unitOfStudy: string;
  emailTemplate: string;
  emailSignature: string;
  emailTemplateVariables: Record<string, any>;
  fromEmail: string;
  replyToEmail: string;
  preferredName: string;
}
interface CreateNotificationParams {
  sendAt: string;
  recipientsAll: boolean;
  recipientsCSV?: File;
  emailNotification?: boolean;
  emailTemplate?: string;
  emailSignature?: string;
  emailTemplateVariables?: string;
  // push notification specific
  title: string;
  body: string;
  link: string;
  buttonLabel?: string;
  pushNotification?: boolean;
  shortTermAnnouncementExpiryDate?: string;
  notificationType?: string;
  shortTermAnnouncement?: boolean;
  sendPushNotification?: boolean;
}
type NotificationServiceRequests = {
  isHttpError: (err: any) => boolean;
  submitForReview: (params: SubmitForReviewParams) => Promise<void>;
  sendTestEmail: (params: SendTestEmailParams) => Promise<void>;
  importEmailRecipients: (params: ImportRecipientsParams) => Promise<void>;
  createNotification: (
    params: CreateNotificationParams,
  ) => Promise<Notification>;
  updateNotification: (
    uuid: string,
    title: string,
    body: string,
    link: string,
    sendAt: string,
    recipientsAll: boolean,
    recipientsCSV?: File,
    pushNotification?: boolean,
    emailNotification?: boolean,
    emailTemplate?: string,
    emailSignature?: string,
    emailTemplateVariables?: string,
  ) => Promise<Notification>;
  updateNotificationRecipients: (
    uuid: string,
    recipientsCSV: File,
  ) => Promise<Notification>;
  getNotification: (uuid: string) => Promise<Notification>;
  getNotificationUserCount: (uuid: string) => Promise<number>;
  getUserNotifications: (args?: {
    status?: string;
    search?: string;
    returnRecipientCount?: boolean;
    pagination?: { limit: number; nextPageToken?: string };
  }) => Promise<
    | {
        count: number;
        items: Notification[];
        nextPageToken?: string;
      }
    | undefined
  >;
  getPendingNotifications: () => Promise<Notification[]>;
  getNotifications: (
    status: string,
    pagination?: { limit: number; nextPageToken?: string },
  ) => Promise<
    | {
        count: number;
        items: Notification[];
        nextPageToken?: string;
      }
    | undefined
  >;

  getRecipientNotificationCount: (uuid: string) => Promise<number>;
  getApprovedNotifications: () => Promise<Notification[]>;
  approveNotification: (
    uuid: string,
    approved: boolean,
  ) => Promise<Notification>;
  revokeNotification: (uuid: string) => Promise<Notification>;
  getEmailTemplates: (automated?: boolean) => Promise<EmailTemplateList>;
  getEmailTemplate: (name: string) => Promise<EmailTemplate>;
};

const notificationServiceRequests: NotificationServiceRequests = {
  isHttpError: (err: any) => {
    return axios.isAxiosError(err);
  },
  createNotification: async ({
    sendAt,
    recipientsAll,
    recipientsCSV,
    pushNotification,
    emailNotification,
    emailTemplate,
    emailSignature,
    emailTemplateVariables,
    // push notification specific parameters
    title,
    body,
    link,
    notificationType,
    shortTermAnnouncement,
    sendPushNotification,
    buttonLabel,
    shortTermAnnouncementExpiryDate,
  }: CreateNotificationParams) => {
    const session = await Auth.currentSession();
    const formData = new FormData();
    formData.append("title", title);
    formData.append("body", body);
    formData.append("sendAt", sendAt);
    formData.append("recipientsAll", recipientsAll.toString());
    formData.append("notificationType", notificationType || "");
    if (link) {
      formData.append("link", link);
    }
    if (notificationType) {
      formData.append("notificationType", notificationType);
    }
    if (shortTermAnnouncement) {
      formData.append(
        "shortTermAnnouncement",
        shortTermAnnouncement.toString(),
      );
    }
    if (sendPushNotification) {
      formData.append("sendPushNotification", sendPushNotification.toString());
    }
    if (buttonLabel) {
      formData.append("buttonLabel", buttonLabel);
    }
    if (shortTermAnnouncementExpiryDate) {
      formData.append(
        "shortTermAnnouncementExpiryDate",
        shortTermAnnouncementExpiryDate,
      );
    }
    if (recipientsCSV) {
      formData.append(`recipients`, recipientsCSV, recipientsCSV.name);
    }
    if (pushNotification) {
      formData.append("pushNotification", pushNotification.toString());
    }
    if (emailNotification) {
      formData.append("emailNotification", emailNotification.toString());
    }
    if (emailTemplate) {
      formData.append("emailTemplate", emailTemplate);
    }
    if (emailSignature) {
      formData.append("emailSignature", emailSignature);
    }
    if (emailTemplateVariables) {
      formData.append("emailTemplateVariables", emailTemplateVariables);
    }
    console.log("pre create notification");
    const res = await axios.request({
      method: "post",
      url: `${API_HOST}/pushnotifications`,
      data: formData,
      headers: {
        Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
        "Content-Type": "multipart/form-data",
      },
    });
    console.log("create notification", res.status);
    if (res.status === 200) {
      return res.data;
    }
    throw new Error("Unable to save result");
  },
  submitForReview: async ({
    emailSubject,
    emailTemplate,
    emailTemplateVariables,
    fromEmail,
    replyToEmail,
    recipientsCSV,
  }: SubmitForReviewParams) => {
    try {
      const session = await Auth.currentSession();
      const formData = new FormData();
      formData.append("title", emailSubject);
      // body is not used for email with template
      formData.append("body", emailTemplateVariables["PersonalMessage"] || "");
      formData.append("link", "");
      formData.append("sendAt", new Date().toISOString());
      formData.append("pushNotification", "false");
      formData.append("emailNotification", "true");
      formData.append("emailTemplate", emailTemplate);
      formData.append(
        "emailTemplateVariables",
        JSON.stringify(emailTemplateVariables),
      );

      formData.append("fromEmail", fromEmail);
      formData.append("replyToEmail", replyToEmail);
      if (recipientsCSV) {
        formData.append(`recipients`, recipientsCSV, recipientsCSV.name);
      }
      console.log(
        "Submit for review request:",
        Object.fromEntries(formData.entries()),
      );

      const res = await axios.request({
        method: "post",
        url: `${API_HOST}/pushnotifications`,
        data: formData,
        headers: {
          Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("Submit for review response:", res.data);

      if (res.status === 200) {
        return res.data;
      }
      throw new Error("Unable to save result");
    } catch (error) {
      // Re-throwing so it can be handled elsewhere
      console.error("Submit for review error:", error);
      throw error;
    }
  },
  importEmailRecipients: async ({
    emailTemplate,
    emailTemplateVariables,
    recipientsCSV,
    fromEmail,
    replyToEmail,
  }: ImportRecipientsParams) => {
    try {
      const session = await Auth.currentSession();
      const formData = new FormData();
      formData.append("emailTemplate", emailTemplate);
      formData.append(
        "emailTemplateVariables",
        JSON.stringify(emailTemplateVariables),
      );
      if (recipientsCSV) {
        formData.append(`recipients`, recipientsCSV, recipientsCSV.name);
      }
      formData.append("fromEmail", fromEmail || "");
      formData.append("replyToEmail", replyToEmail || "");
      console.log(
        "Import email recipients request:",
        Object.fromEntries(formData.entries()),
      );

      const res = await axios.request({
        method: "post",
        url: `${API_HOST}/importemailrecipients`,
        data: formData,
        headers: {
          Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("Import email recipients response:", res.data);

      if (res.status === 200) {
        return res.data;
      }
      throw new Error("Unable to import email recipients");
    } catch (error) {
      // Re-throwing so it can be handled elsewhere
      console.error("Import email recipients error:", error);
      throw error;
    }
  },
  sendTestEmail: async ({
    emailTo,
    emailSubject,
    emailBody,
    unitOfStudy,
    emailTemplate,
    emailSignature,
    emailTemplateVariables,
    fromEmail,
    replyToEmail,
    preferredName,
  }: SendTestEmailParams) => {
    const session = await Auth.currentSession();

    const requestBody = {
      to: emailTo,
      subject: emailSubject,
      body: emailBody,
      emailTemplate: emailTemplate,
      unitOfStudy: unitOfStudy,
      emailSignature: emailSignature,
      emailTemplateVariables: emailTemplateVariables,
      fromEmail: fromEmail,
      replyToEmail: replyToEmail,
      preferredName: preferredName,
    };

    console.log("Sending JSON Request:", requestBody);

    const res = await axios.post(`${API_HOST}/sendtestemail`, requestBody, {
      headers: {
        Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
        "Content-Type": "application/json",
      },
    });

    console.log("Response:", res.status);

    if (res.status === 200) {
      return res.data;
    }

    throw new Error("Unable to send test email");
  },
  updateNotification: async (
    uuid: string,
    title: string,
    body: string,
    link: string,
    sendAt: string,
    recipientsAll: boolean,
    recipientsCSV?: File,
    pushNotification?: boolean,
    emailNotification?: boolean,
    emailTemplate?: string,
    emailSignature?: string,
    emailTemplateVariables?: string,
  ) => {
    const session = await Auth.currentSession();
    const formData = new FormData();
    formData.append("title", title);
    formData.append("body", body);
    formData.append("link", link);
    formData.append("sendAt", sendAt);
    formData.append("recipientsAll", recipientsAll.toString());
    if (recipientsCSV) {
      formData.append(`recipients`, recipientsCSV, recipientsCSV.name);
    }
    if (pushNotification) {
      formData.append("pushNotification", pushNotification.toString());
    }
    if (emailNotification) {
      formData.append("emailNotification", emailNotification.toString());
    }
    if (emailTemplate) {
      formData.append("emailTemplate", emailTemplate);
    }
    if (emailSignature) {
      formData.append("emailSignature", emailSignature);
    }
    if (emailTemplateVariables) {
      formData.append("emailTemplateVariables", emailTemplateVariables);
    }
    const res = await axios.request({
      method: "put",
      url: `${API_HOST}/pushnotifications/id/${uuid}`,
      data: formData,
      headers: {
        Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
        "Content-Type": "multipart/form-data",
      },
    });
    if (res.status === 200) {
      return res.data;
    }
    throw new Error("Unable to save result");
  },
  updateNotificationRecipients: async (uuid: string, recipientsCSV: File) => {
    const session = await Auth.currentSession();
    const formData = new FormData();
    formData.append(`recipients`, recipientsCSV, recipientsCSV.name);
    const res = await axios.request({
      method: "put",
      url: `${API_HOST}/pushnotifications/id/${uuid}/recipients`,
      data: formData,
      headers: {
        Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
        "Content-Type": "multipart/form-data",
      },
    });
    if (res.status === 200) {
      return res.data;
    }
    throw new Error("Unable to save result");
  },
  getNotification: async (uuid: string) => {
    const session = await Auth.currentSession();
    const res = await axios.get(`${API_HOST}/pushnotifications/id/${uuid}`, {
      headers: {
        Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
      },
    });
    if (res.status === 200 && res.data.Item) {
      return res.data.Item;
    }
    throw new Error("Unable to get result");
  },
  getNotificationUserCount: async (uuid: string) => {
    const session = await Auth.currentSession();
    const res = await axios.get(
      `${API_HOST}/pushnotifications/id/${uuid}/usercount`,
      {
        headers: {
          Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
        },
      },
    );
    if (res.status === 200 && res.data) {
      return res.data.count ?? 0;
    }
    throw new Error("Unable to get result");
  },
  getEmailTemplates: async (automated?: boolean) => {
    const session = await Auth.currentSession();
    const url = automated
      ? `${API_HOST}/email-templates?automated=true`
      : `${API_HOST}/email-templates`;
    const res = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
      },
    });
    if (res.status === 200 && res.data.templates) {
      return res.data.templates;
    }
    throw new Error("Unable to fetch the email templates");
  },
  getEmailTemplate: async (templateName: string) => {
    const session = await Auth.currentSession();
    const res = await axios.get(`${API_HOST}/email-templates/${templateName}`, {
      headers: {
        Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
      },
    });
    if (res.status === 200 && res.data.template) {
      return res.data.template;
    }
    throw new Error(`Unable to fetch the email template ${templateName}`);
  },
  getUserNotifications: async ({
    status,
    search,
    returnRecipientCount = false,
    pagination = { limit: 100 },
  }: {
    status?: string;
    search?: string;
    returnRecipientCount?: boolean;
    pagination?: { limit: number; nextPageToken?: string };
  } = {}) => {
    const session = await Auth.currentSession();
    const nextPageToken = pagination.nextPageToken
      ? `&nextPageToken=${pagination.nextPageToken}`
      : "";
    const searchQuery = search ? `&search=${search}` : "";
    const statusQuery = status ? `&status=${status}` : "";
    const returnRecipientCountQuery = returnRecipientCount
      ? `&returnRecipientCount=${returnRecipientCount}`
      : "";
    const url = `${API_HOST}/pushnotifications?limit=${pagination.limit}${statusQuery}${nextPageToken}${searchQuery}${returnRecipientCountQuery}`;
    const res = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
      },
    });
    if (res.status === 200 && res.data.items) {
      return {
        count: res.data.count,
        items: res.data.items,
        nextPageToken: res.data.nextPageToken,
      };
    }
    throw new Error("Unable to get result");
  },
  getNotifications: async (
    status: string,
    pagination: { limit: number; nextPageToken?: string } = { limit: 10 },
  ) => {
    const session = await Auth.currentSession();
    const nextPageToken = pagination.nextPageToken
      ? `&nextPageToken=${pagination.nextPageToken}`
      : "";
    try {
      const res = await axios.get(
        `${API_HOST}/notifications?status=${status}&limit=${pagination.limit}${nextPageToken}`,
        {
          headers: {
            Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
          },
        },
      );
      if (res.status === 200 && res.data.items) {
        return {
          count: res.data.count,
          items: res.data.items,
          nextPageToken: res.data.nextPageToken,
        };
      }
    } catch (error) {
      console.error("getNotifications error:", error);
      throw error;
    }
  },
  getPendingNotifications: async () => {
    const session = await Auth.currentSession();
    const res = await axios.get(`${API_HOST}/pushnotifications/pending`, {
      headers: {
        Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
      },
    });
    if (res.status === 200 && res.data.Items) {
      return res.data.Items.sort((a: Notification, b: Notification) => {
        const aDate = new Date(a.SendAt);
        const bDate = new Date(b.SendAt);
        return bDate.getTime() - aDate.getTime();
      });
    }
    throw new Error("Unable to get result");
  },
  getRecipientNotificationCount: async (uuid: string) => {
    const session = await Auth.currentSession();
    const res = await axios.get(
      `${API_HOST}/pushnotifications/id/${uuid}/recipientcount`,
      {
        headers: {
          Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
        },
      },
    );
    if (res.status === 200 && res.data) {
      return res.data.count ?? 0;
    }
    throw new Error("Unable to get result");
  },
  getApprovedNotifications: async () => {
    const session = await Auth.currentSession();
    const res = await axios.get(`${API_HOST}/pushnotifications/approved`, {
      headers: {
        Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
      },
    });
    if (res.status === 200 && res.data.Items) {
      return res.data.Items.sort((a: Notification, b: Notification) => {
        const aDate = new Date(a.SendAt);
        const bDate = new Date(b.SendAt);
        return bDate.getTime() - aDate.getTime();
      });
    }
    throw new Error("Unable to get result");
  },
  approveNotification: async (uuid: string, approved: boolean) => {
    const session = await Auth.currentSession();
    const res = await axios.put(
      `${API_HOST}/pushnotifications/authorise/${uuid}`,
      {
        approved: approved,
      },
      {
        headers: {
          Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
        },
      },
    );
    if (res.status === 200) {
      return res.data;
    }
    throw new Error("Unable to save result");
  },
  revokeNotification: async (uuid: string) => {
    const session = await Auth.currentSession();
    const res = await axios.put(
      `${API_HOST}/pushnotifications/revoke/${uuid}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
        },
      },
    );
    if (res.status === 200) {
      return res.data;
    }
    throw new Error("Unable to save result");
  },
};

export default notificationServiceRequests;
